import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable, Injector, OnDestroy } from "@angular/core";
import { doc, Firestore, getDoc } from "@angular/fire/firestore";
import { MatDialog } from "@angular/material/dialog";
import { Data, Router } from "@angular/router";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { FirebaseError } from "firebase/app";
import { compare } from "compare-versions";
import type { DeviceDetails } from "@interfaces/deviceDetails.i";
import { onSnapshot, serverTimestamp, Timestamp } from "firebase/firestore";
import {
  BehaviorSubject,
  catchError,
  defer,
  delay,
  firstValueFrom,
  map,
  Observable,
  of,
  retry,
  Subscription,
  switchMap,
  take
} from "rxjs";
import { CommonVariable } from "src/app/app.constants";
import { AppUpdateComponent } from "src/app/components/app-update/app-update.component";
import { environment } from "src/environments/environment";
import { CommonService } from "../common/common.service";
import { FirestoreDataService } from "../firestore-data.service";
import { NativeSwUpdateService } from "../native-sw-update.service";
import { SWServiceService } from "../serviceWorkers/swservice.service";
import { UserManagementService } from "../user-management.service";

@Injectable({
  providedIn: "root",
})
export class AppVersionService implements OnDestroy {
  appUpdateOnLoginTrigger$ = new BehaviorSubject(false);
  private updatePopUpAlreadyThere = false;
  private updatedVersionData: Data;
  lastLoginDate: Date;
  appVersionMajor: number;
  appVersionMinor: number;
  userVersionMajor: number;
  userVersionMinor: number;
  appVersionPatch: number;
  userVersionPatch: number;
  discritLink: string;
  discritLink$: Observable<string>;
  userPWAVersion: string;
  latestAppVersion: string;
  private versionCheckAfterLogin = false;
  private delayTwoMin = 120000; // 2 minutes
  private appVersionSubscription: Subscription;
  private userVersionSubscription: Subscription;
  private userFingerPrintId: string;
  private injector: Injector;
  private commonService = inject(CommonService);
  private globalVersionUnsub?: () => void;
  get pwaVersionPath(): string {
    return environment.pwaVersionCheckPath;
  }
  constructor(
    private swservice: SWServiceService,
    private firestore: Firestore,
    private http: HttpClient,
    private router: Router,
    private userManagementService: UserManagementService,
    private matDialog: MatDialog,
    private firestoreDataService: FirestoreDataService,
    private nativeSwUpdate: NativeSwUpdateService,
  ) {
    this._getUserLastLoginDate();
    setTimeout(() => {
      this.triggerAppUpdate();
      this.checkAppUpdateOnLogin();
    }, 5000);

    this._getUserVersion$();
  }

  private toSemver(v: unknown): string {
    const raw = String(v ?? "").trim().replace(/^v/i, "");
    const match = raw.match(/\d+(?:\.\d+){0,2}/);

    if (!match) return "0.0.0";

    const [major = "0", minor = "0", patch = "0"] = match[0].split(".");
    return `${Number(major) || 0}.${Number(minor) || 0}.${Number(patch) || 0}`;
  }

  private compareSafe(a: unknown, b: unknown, operator: ">" | "<" | "="): boolean {
    return compare(this.toSemver(a), this.toSemver(b), operator);
  }

  triggerAppUpdate() {
    this.appUpdateOnLoginTrigger$?.next(true);
  }

  private async refreshServiceWorkerRegistration(): Promise<void> {
    if (!("serviceWorker" in navigator)) return;

    const registration = await navigator.serviceWorker.getRegistration("/");
    await registration?.update();
  }

  public async checkForUpdates(): Promise<void> {
    try {
      await firstValueFrom(
        defer(async () => {
          await this.refreshServiceWorkerRegistration();

          // Wait 10 seconds for service worker to be ready
          await new Promise((resolve) => setTimeout(resolve, 10000));

          // Get Firestore document for app version
          const docRef = doc(this.firestore, this.pwaVersionPath);
          const docSnap = await getDoc(docRef);

          const globalVersion = (docSnap.data() ?? {}) as { version?: string };

          // Check for legacy service worker
          const isLegacyServiceWorker = await firstValueFrom(
            this.swservice.checkLegacyServiceWorkerRegistration().pipe(
              retry({ count: 20, delay: 5000 })
            )
          );

          let workerVersion: { version: string } = { version: "0.0.0" };

          if (!isLegacyServiceWorker) {
            // Try to get worker version via API
            workerVersion = await firstValueFrom(
              this.http.get("/api/service-worker-version", { responseType: "text" }).pipe(
                retry({ count: 20, delay: 5000 }),
                map((response: string) => {
                  try {
                    return JSON.parse(response);
                  } catch (error) {
                    console.warn(
                      "Invalid JSON response from service worker version API:",
                      response
                    );
                    return { version: "0.0.0" };
                  }
                }),
                catchError((error: HttpErrorResponse) => {
                  console.error("Failed to fetch worker version:", error);
                  return of({ version: "0.0.0" });
                })
              )
            );
          }

          // If popup already shown, do nothing
          if (this.updatePopUpAlreadyThere) {
            return;
          }

          // Compare versions using semantic versioning
          const isNewerVersion = this.compareSafe(globalVersion?.version, workerVersion?.version, ">" );

          if (isNewerVersion) {
            this.updatedVersionData = globalVersion;

            // Get allowed pages from Firestore
            try {
              const allowedPagesRef = doc(this.firestore, environment.ALLOWED_PAGES_PATH);
              const allowedPagesSnap = await getDoc(allowedPagesRef);
              const allowedPages = (allowedPagesSnap.data() ?? { list: [] }) as {
                list?: string[];
              };

              const shouldPrompt =
                allowedPages?.list?.some((page) => this.router.url.includes(page)) &&
                !this.router.url.startsWith("/account-created");

              if (shouldPrompt) {
                this.promptUser();
              }
            } catch (err) {
              console.warn("Error fetching allowed pages:", err);
            }
          }
        })
      );
    } catch (err) {
      console.error("Error in checkForUpdates:", err);
    }
  }


  checkDeviceAppVersion(fingerPrint: string) {
    if (!fingerPrint) return;
    this.upsertDeviceAppVersion(fingerPrint);
  }

  private async upsertDeviceAppVersion(fingerPrint: string) {
    try {
      const versionDocRef = doc(this.firestore, this.pwaVersionPath);
      const versionDocSnapshot = await getDoc(versionDocRef);
      const versionData = versionDocSnapshot?.data() as { updated_at: Date; version: string | number };
      this.updatedVersionData = versionData;

      const deviceDocRef = doc(this.firestore, `/devices/${fingerPrint}`);
      const deviceDocSnapshot = await getDoc(deviceDocRef);
      let deviceData = deviceDocSnapshot?.data() as DeviceDetails;

      // Get userId from CommonService
      this.commonService.getUid("AppVersionService").pipe(take(1)).subscribe(async (userId) => {
        if (!userId) {
          const requestBody: DeviceDetails = {
            agent: navigator?.userAgent,
            count: 1,
            sw_hash: "",
            sw_updated_at: new Date(),
            userIp: CommonVariable.userIP,
            appVersion: this.toSemver(versionData.version),
          };

          const path_id = "6DeQhJF4mlxDzwSFuWZU";
          const doc_data = requestBody;
          const dv = { docId: fingerPrint };
          const method = "update";

          this.firestoreDataService.getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data,
            dv,
            method,
          }).subscribe({
            error: (err) => console.error('Error in updateUserUdid:', err)
          });
          return;
        }

        const userVersionDocRef = doc(this.firestore, `/users/${userId}/pwa_version/user_version`);
        const userVersionDocSnapshot = await getDoc(userVersionDocRef);

        if (userVersionDocSnapshot.exists()) {
          const userPWAVersion = userVersionDocSnapshot?.data() as Data;

          if (versionData && versionData?.updated_at && versionData?.version && userPWAVersion) {
            const isNewerVersion = this.compareSafe(
              versionData.version,
              userPWAVersion.version,
              '>'
            );
            if (isNewerVersion) {
              const allowedPagesDocRef = doc(this.firestore, environment.ALLOWED_PAGES_PATH);
              const allowedPagesDocSnapshot = await getDoc(allowedPagesDocRef);
              const allowedPages = allowedPagesDocSnapshot?.data() ?? { list: [] };

              if (allowedPages?.list?.some((d) => this.router.url.includes(d)) &&
                !this.router.url.startsWith("/account-created")) {
                if (CommonVariable.FingerprintJSConst) {
                  // this.syncPWAVersions();
                }
                 this.promptUser();
              }
            }
          }

          // Check if deviceData exists before accessing its properties
          if (deviceData) {
            deviceData.count = deviceData.count + 1;
          } else {
            // Initialize deviceData if it doesn't exist
            deviceData = {
              agent: navigator?.userAgent,
              count: 1,
              sw_hash: "",
              sw_updated_at: new Date(),
              userIp: CommonVariable.userIP,
              appVersion: this.toSemver(versionData.version),
            };
          }

          const path_id = "6DeQhJF4mlxDzwSFuWZU";
          const doc_data = deviceData;
          const dv = { docId: fingerPrint };
          const method = "update";

          this.firestoreDataService.getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data,
            dv,
            method,
          }).subscribe({
            error: (err) => console.error('Error in updateUserUdid:', err)
          });
        } else {
          // Update PWA in users collection if the document is empty
          this.updatePwaVersion(versionData);

          const requestBody: DeviceDetails = {
            agent: navigator?.userAgent,
            count: 1,
            sw_hash: "",
            sw_updated_at: new Date(),
            userIp: CommonVariable.userIP,
            appVersion: this.toSemver(versionData.version),
          };

          const path_id = "6DeQhJF4mlxDzwSFuWZU";
          const doc_data = requestBody;
          const dv = { docId: fingerPrint };
          const method = "update";

          this.firestoreDataService.getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data,
            dv,
            method,
          }).subscribe({
            error: (err) => console.error('Error in updateUserUdid:', err)
          });
        }
      });
    } catch (err) {
      return err;
    }
  }

  updatePwaVersion(appVersionData: Data): Promise<void> {
    return new Promise((resolve, reject) => {
      this.commonService
        .getUid("AppVersionService")
        .pipe(take(1))
        .subscribe({
          next: (userId) => {
            if (!userId || !appVersionData) {
              resolve();
              return;
            }
            const path_id = "p7tvyag5rpi6mgeJ7oUu";
            const doc_data = appVersionData;
            const method = "update";

            this.firestoreDataService
              .getV2CloudFunction("updatefirestoredoc", {
                path_id,
                doc_data,
                method,
              })
              .subscribe({
                next: () => {
                  console.log('[AppVersion] User PWA version updated');
                  resolve();
                },
                error: (err) => {
                  console.error('Failed to update PWA version:', err);
                  reject(err);
                },
              });
          },
          error: reject,
        });
    });
  }


  async _getUserLastLoginDate(): Promise<void> {
    this.commonService.getUid("AppVersionService")
      .pipe(take(1))
      .subscribe(async (userId) => {
        if (!userId) {
          return;
        }

        try {
          // one-shot read
          const snap = await getDoc(doc(this.firestore, `users/${userId}`));

          if (!snap.exists()) {
            console.error({ status: 404, error: 'Document not found' });
            return;
          }

          const data = snap.data() as Data;

          if (!data.last_login_datetime) {
            console.error({ status: 404, error: 'Login Date is undefined' });
            return;
          }

          /* Handle both Firestore Timestamp and plain {seconds,nanoseconds} */
          const ts = data.last_login_datetime as unknown;
          this.lastLoginDate =
            ts instanceof Timestamp
              ? ts.toDate()
              : new Date(((ts as { seconds: number }).seconds || 0) * 1_000);
        } catch (err) {
          console.error('Error fetching last login date:', err);
        }
      });
  }

  async _getUserVersion$(): Promise<void> {
    try {
      /* --------------------------------
       * Step 1: Fetch Global App Version
       * -------------------------------- */

      const globalRef = doc(this.firestore, this.pwaVersionPath);

      this.globalVersionUnsub = onSnapshot(globalRef, (versionSnap) => {
        const versionData = versionSnap?.data() as { version?: string };
        if (versionData?.version) {
          this.latestAppVersion = this.toSemver(versionData.version).split('.').slice(0, 3).join('.');
          const [major1, minor1, patch1] = this.latestAppVersion.split('.');
          this.appVersionMajor = +major1;
          this.appVersionMinor = +minor1;
          this.appVersionPatch = +patch1;
          if (this.userPWAVersion) {
            this.verifyUserVersion();
          }
        }
      });

      /* --------------------------------
       * Step 2: Fetch User App Version
       * -------------------------------- */
      this.commonService.getUid("AppVersionService").pipe(
        take(1),
        switchMap(async (userId) => {
          if (!userId) {
            return of(null);
          }
          const userDocPath = `/users/${userId}/pwa_version/user_version`;
          try {
            const userVersionSnap = await getDoc(doc(this.firestore, userDocPath));
            const userVersionData = userVersionSnap?.data() as { version?: string };
            if (userVersionData?.version) {
              this.userPWAVersion = this.toSemver(userVersionData.version).split('.').slice(0, 3).join('.');
              const [major2, minor2, patch2] = this.userPWAVersion.split(".");
              this.userVersionMajor = +major2;
              this.userVersionMinor = +minor2;
              this.userVersionPatch = +patch2;
              this.verifyUserVersion();
            }
          } catch (err) {
            // Silent fail for new users
          }
          return of(null);
        })
          ).subscribe();
        } catch (err) {
          // Silent fail - version check will retry
        }
      }

  verifyUserVersion(): void {
    setTimeout(() => {
      if (!this.latestAppVersion || !this.userPWAVersion) {
        console.warn('[VersionCheck] Version strings not ready', {
          latest: this.latestAppVersion,
          user: this.userPWAVersion
        });
        return;
      }

      const isNewer = this.compareSafe(this.latestAppVersion, this.userPWAVersion, '>');
      const isSame = this.compareSafe(this.latestAppVersion, this.userPWAVersion, '=');
      const isOlder = this.compareSafe(this.latestAppVersion, this.userPWAVersion, '<');

      if (isNewer) {
        console.warn('[VersionCheck] New version available -> force update', {
          latest: this.latestAppVersion,
          user: this.userPWAVersion
        });
        this.showPopup();
        return;
      }

      if (isSame) {
        console.log('[VersionCheck] Version up to date', {
          version: this.latestAppVersion
        });
        return;
      }

      if (isOlder) {
        // User fingerprint version is ahead of global - do not prompt for an update.
        console.info('[VersionCheck] User version ahead of global (no update needed)', {
          latest: this.latestAppVersion,
          user: this.userPWAVersion
        });
      }
    }, 20000);
  }


  async showPopup(): Promise<void> {
    this.updatedVersionData = {
      ...(this.updatedVersionData ?? {}),
      version: this.latestAppVersion,
    };
    this.promptUser();
  }

  async updateUserByUsingLink(): Promise<void> {
    try {
      await this.updatePwaVersion(this.updatedVersionData);
      await this.updateUserPwaNodes();

      this.versionCheckAfterLogin = false;

      // Optional delay to allow operations to settle before reload
      await new Promise((resolve) => setTimeout(resolve, 1000));

      try {
        await this.nativeSwUpdate.activateUpdate();
      } catch (activateErr) {
        console.warn('SW activate failed, reloading anyway:', activateErr);
      }
      location.reload();
    } catch (err) {
      console.error("Error updating user via link:", err);
    }
  }


  async _getUserVersionAfterLogin$(): Promise<void> {
    try {
      // Get the latest global app version from Firestore
      const globalDocRef = doc(this.firestore, this.pwaVersionPath);
      const globalSnap = await getDoc(globalDocRef);

      if (globalSnap.exists()) {
        const docData = globalSnap.data() as { version: string };
        const version = docData.version?.toString();
        if (version) {
          this.latestAppVersion = version;
          const [major1, minor1, patch1] = version.split(".");
          this.appVersionMajor = +major1;
          this.appVersionMinor = +minor1;
          this.appVersionPatch = +patch1;
        } else {
          console.error("Latest App Version is not a string.");
        }
      } else {
        console.warn("Global version document does not exist.");
      }

      // Get the user's PWA version
      this.userFingerPrintId = CommonVariable.FingerprintJSConst || localStorage.getItem("fingerSalt");

      this.commonService.getUid("AppVersionService")
        .pipe(take(1))
        .subscribe(async (userId) => {
          if (!userId || !this.userFingerPrintId) {
            console.warn("UserId or Fingerprint not found.");
            return;
          }

          try {
            const userDocRef = doc(this.firestore, `/users/${userId}/pwa_version/${this.userFingerPrintId}`);
            const userSnap = await getDoc(userDocRef);

            if (userSnap.exists()) {
              const userData = userSnap.data() as { current_version: string };
              const version = userData.current_version?.toString();
              if (version) {
                this.userPWAVersion = version;
                const [major2, minor2, patch2] = version.split(".");
                this.userVersionMajor = +major2;
                this.userVersionMinor = +minor2;
                this.userVersionPatch = +patch2;

                this.verifyUserVersion();
              } else {
                console.warn("User PWA Version is not a string.");
              }
            } else {
              console.warn("User version document does not exist (expected for new users).");
            }
          } catch (err) {
            // Handle permission-denied or not-found errors gracefully (expected for new users)
            if (err instanceof FirebaseError && (err.code === 'permission-denied' || err.code === 'not-found')) {
              console.warn('[AppVersionService] _getUserVersionAfterLogin$ - User version document access error (expected for new users):', err.message);
            } else {
              console.error('[AppVersionService] _getUserVersionAfterLogin$ - Unexpected error:', err);
            }
          }
        });

    } catch (error) {
      console.error("Error in _getUserVersionAfterLogin$:", error);
    }
  }


  private checkAppUpdateOnLogin() {
    this.appUpdateOnLoginTrigger$?.subscribe({
      next: (status) => {
        if (status) {
          this.versionCheckAfterLogin = true;
          this._getUserVersionAfterLogin$();
        }
      },
      error: (err) => {
        return err;
      },
    });
  }

  async updateUserPwaNodes(force = false): Promise<void> {
    console.log("[AppVersionService] updateUserPwaNodes called");

    if (!force && !this.versionCheckAfterLogin) {
      console.log("[AppVersionService] Skipped -> versionCheckAfterLogin=false");
      return;
    }

    if (force && !this.latestAppVersion) {
      console.warn("[AppVersionService] Skipped force update -> latestAppVersion missing");
      return;
    }

    try {
      // 1) Get userId
      const userId = await firstValueFrom(
        this.commonService.getUid("AppVersionService").pipe(take(1))
      );

      if (!userId) {
        console.warn("[AppVersionService] Skipped -> userId missing");
        return;
      }

      // 2) Ensure fingerprintId
      const fingerprintId =
        this.userFingerPrintId ?? (await this.fingerprintJS());

      if (!fingerprintId) {
        console.error("[AppVersionService] Fingerprint ID could not be resolved");
        return;
      }

      console.log("[AppVersionService] Proceeding with PWA node update", {
        userId,
        fingerprintId,
        latestAppVersion: this.latestAppVersion,
      });

      // 3) Update root doc sw_update_required
      {
        const path_id = "yzF1Ss5eiMqnh91zQMtv";
        const doc_data = { sw_update_required: false };
        const method = "update";

        console.log("[AppVersionService] Updating root user node", {
          path_id,
          doc_data,
        });

        this.firestoreDataService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        }).subscribe({
          next: () => console.log("[AppVersionService] Root user node updated"),
          error: (err) => console.error("[AppVersionService] Failed root update", err),
        });
      }

      // 4) Update fingerprint-specific sub-doc
      {
        const path_id = "0dZ4B4cU95eROzb4fvtf";
        const doc_data = { sw_updated_at: serverTimestamp() };
        const dv = { docId: fingerprintId, userId }; // <-- include userId
        const method = "update";

        console.log("[AppVersionService] Updating fingerprint node", { path_id, dv });

        this.firestoreDataService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          dv,
          method,
        }).subscribe({
          next: () => {
            localStorage.setItem(
              'acceptedAppVersion',
              String(this.latestAppVersion)
            );
            console.log("[AppVersionService] Fingerprint node updated")
          },

          error: (err) =>
            console.error("[AppVersionService] Failed fingerprint update", err),
        });
      }

      // 5) Update user fingerprint request (current_version, agent, IP)
      await this.updateUserFingerprintRequest(this.latestAppVersion);

      console.log("[AppVersionService] updateUserPwaNodes completed successfully");
    } catch (error) {
      console.error("[AppVersionService] updateUserPwaNodes ERROR", error);
      throw error;
    }
  }


  public async updateUserFingerprintRequest(appVersionId: string) {
    try {
      const ipInfo: Data = await firstValueFrom(
        this.userManagementService.getUserCountryBaseOnIP()
      );

      const token = localStorage.getItem("user_auto_login_token");
      const fingerPrint = this.userFingerPrintId ?? (token ? null : await this.fingerprintJS());

      const userId = await firstValueFrom(
        this.commonService.getUid("AppVersionService").pipe(take(1))
      );

      if (!userId) {
        console.warn("[AppVersionService] Skipped -> userId missing in fingerprint request");
        return;
      }

      const payload = {
        userId,
        fingerprintId: fingerPrint,
        userAgent: navigator.userAgent,
        appVersion: appVersionId ?? null,
        userIP: ipInfo?.ip ?? null,
      };

      console.log("[AppVersionService] Sending fingerprint update request", payload);

      await firstValueFrom(
        this.firestoreDataService.getV2CloudFunction("updateuserfingerprintv2", payload)
      );

      console.log("[AppVersionService] Fingerprint update completed successfully");
    } catch (err) {
      console.error("[AppVersionService] Error updating user fingerprint", err);
    }
  }

  async setUserPWACurrentVersion(userId: string): Promise<void> {
    const acceptedVersion = localStorage.getItem('acceptedAppVersion');
    if (acceptedVersion) {
      return; // already set, do nothing
    }

    const fingerPrintId =
      CommonVariable.FingerprintJSConst ||
      localStorage.getItem('fingerSalt');
    if (!userId || !fingerPrintId) {
      console.warn('[AppVersion] UserId or Fingerprint not found');
      return;
    }

    const userDocRef = doc(
      this.firestore,
      `/users/${userId}/pwa_version/${fingerPrintId}`
    );

    try {
      const userSnap = await getDoc(userDocRef);
      console.log("User version document fetched in set:", userSnap.exists());
      if (!userSnap.exists()) return;

      const { current_version } = userSnap.data() as {
        current_version?: string;
      };
      if (current_version) {
        localStorage.setItem(
          'acceptedAppVersion',
          current_version.toString()
        );
      }
    } catch (err) {
      console.error('[AppVersion] Failed to set accepted version', err);
    }
  }

  async fingerprintJS() {
    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      const visitorId = result.visitorId;
      return visitorId;
    } catch (error) {
      return null; // or handle the error in a suitable way
    }
  }
  public async promptUser(event?: Data) {
    const derivedVersion =
      (event?.version != null ? this.toSemver(event.version) : "") ||
      (this.updatedVersionData?.version != null
        ? this.toSemver(this.updatedVersionData.version)
        : "") ||
      this.latestAppVersion;

    if (!derivedVersion) {
      console.warn('[ForceUpdatePopup] Latest version not available');
      return;
    }

    this.latestAppVersion = String(derivedVersion).split('.').slice(0, 3).join('.');

    if (this.updatePopUpAlreadyThere) return;
    this.updatePopUpAlreadyThere = true;

    // Check allowed pages configuration
    try {
      const allowedPagesRef = doc(this.firestore, environment.ALLOWED_PAGES_PATH);
      const allowedPagesSnap = await getDoc(allowedPagesRef);
      const allowedPages = (allowedPagesSnap.data() ?? { list: [] }) as { list?: string[] };

      const currentUrl = this.router.url;
      const isAllowedPage = allowedPages?.list?.some((page) => currentUrl.includes(page));
      const isNotAccountCreated = !currentUrl.startsWith('/account-created');

      if (!isAllowedPage || !isNotAccountCreated) {
        console.warn('[ForceUpdatePopup] Not on allowed page or on excluded route', {
          currentUrl,
          isAllowedPage,
          isNotAccountCreated
        });
        this.updatePopUpAlreadyThere = false;
        return;
      }
    } catch (err) {
      console.warn('[ForceUpdatePopup] Error checking allowed pages:', err);
      this.updatePopUpAlreadyThere = false;
      return;
    }

    const hashData = localStorage.getItem("sw_hash");
    this.commonService.getUid("AppVersionService").pipe(take(1)).subscribe((userId) => {
      if (userId && userId.length > 6 && event) {
        const path_id = "yzF1Ss5eiMqnh91zQMtv";
        const doc_data = { sw_hash: hashData };
        const method = "update";
        this.firestoreDataService.getV2CloudFunction("updatefirestoredoc", { path_id, doc_data, method })
          .subscribe({
            error: (err) => console.error("Error updating sw_hash via cloud function:", err)
          });
      }
    });

    // Open the update dialog
    const decline = this.matDialog.open(AppUpdateComponent, {
      panelClass: "app-update-dialog-size",
      backdropClass: "decline-back-drop-color",
      disableClose: true,
    });

    decline.afterClosed().pipe(take(1), delay(3000)).subscribe(async () => {
      try {
        await this.updateUserPwaNodes(true);
        this.versionCheckAfterLogin = false;

        const pwaVersion = {
          ...(this.updatedVersionData ?? {}),
          version: this.latestAppVersion,
        };

        await this.updatePwaVersion(pwaVersion);

        // Activate waiting SW before any unregister so SKIP_WAITING can still reach it.
        try {
          await this.nativeSwUpdate.activateUpdate();
        } catch (activateErr) {
          console.warn("SW activate failed, continuing reload:", activateErr);
        }

        if ("caches" in self) {
          const keys = await caches.keys();
          await Promise.all(keys.map((key) => caches.delete(key)));
        } else {
          console.warn("Caches API not supported.");
        }

        this.swservice.unRegisterServiceWorker();
        document.location.reload();
      } catch (err) {
        console.error("Update failed:", err);
        this.updatePopUpAlreadyThere = false;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.globalVersionUnsub) {
      this.globalVersionUnsub();
    }
  }



}
